import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  eventsData: [],
  editedRows: {},
  filters: {
    channel: '',
    direction: '',
    customer_type: '',
    outcome: '',
    priority: '',
    follow_up_required: null,
    recent_unknown: 'hide',
    from_date: null,
    to_date: null,
    search: '',
    patient_id: null,
    case_id: null,
    provider_id: null,
  },
  stats: {
    total_events: 0,
    by_channel: [],
    by_direction: [],
    by_outcome: [],
    by_customer_type: [],
    follow_up_required: 0,
  },
  selectedEvent: null,
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
  pagination: {
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
  },
};

export const vcxEventsSlice = createSlice({
  name: 'vcxEvents',
  initialState,
  reducers: {
    // Set all events data
    setEventsData: (state, action) => {
      state.eventsData = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Set paginated events data
    setPaginatedEventsData: (state, action) => {
      const { events, pagination } = action.payload;
      state.eventsData = events;
      state.pagination = pagination;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Add or update a single event
    updateEvent: (state, action) => {
      const updatedEvent = action.payload;
      const index = state.eventsData.findIndex(e => e.id === updatedEvent.id);
      if (index !== -1) {
        state.eventsData[index] = { ...state.eventsData[index], ...updatedEvent };
      } else {
        state.eventsData.push(updatedEvent);
      }
    },

    // Delete event
    deleteEvent: (state, action) => {
      const eventId = action.payload;
      state.eventsData = state.eventsData.filter(e => e.id !== eventId);
      if (state.selectedEvent?.id === eventId) {
        state.selectedEvent = null;
      }
    },

    // Track cell edit (before saving to API)
    trackCellEdit: (state, action) => {
      const { eventId, field, value } = action.payload;

      if (!state.editedRows[eventId]) {
        state.editedRows[eventId] = {};
      }

      state.editedRows[eventId][field] = value;

      // Update the local data immediately for UI responsiveness
      const index = state.eventsData.findIndex(e => e.id === eventId);
      if (index !== -1) {
        state.eventsData[index] = {
          ...state.eventsData[index],
          [field]: value
        };
      }
    },

    // Clear all edited rows after successful save
    clearEditedRows: (state) => {
      state.editedRows = {};
    },

    // Clear edited rows for a specific event
    clearEditedRowsForEvent: (state, action) => {
      const eventId = action.payload;
      delete state.editedRows[eventId];
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Set stats data
    setStats: (state, action) => {
      state.stats = action.payload;
    },

    // Set selected event (for detail view)
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setEventsData,
  setPaginatedEventsData,
  updateEvent,
  deleteEvent,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForEvent,
  setFilters,
  resetFilters,
  setStats,
  setSelectedEvent,
  setPagination,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
} = vcxEventsSlice.actions;

// Selectors
export const selectEventsData = (state) => state.vcxEvents.eventsData;
export const selectEditedRows = (state) => state.vcxEvents.editedRows;
export const selectFilters = (state) => state.vcxEvents.filters;
export const selectStats = (state) => state.vcxEvents.stats;
export const selectSelectedEvent = (state) => state.vcxEvents.selectedEvent;
export const selectPagination = (state) => state.vcxEvents.pagination;
export const selectLoading = (state) => state.vcxEvents.loading;
export const selectRefreshing = (state) => state.vcxEvents.refreshing;
export const selectSaving = (state) => state.vcxEvents.saving;
export const selectError = (state) => state.vcxEvents.error;
export const selectLastFetched = (state) => state.vcxEvents.lastFetched;

export default vcxEventsSlice.reducer;
